import axios from "axios";

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 30000,
    headers: {
        "Content-Type": "application/json",
    },
});

// Attach token to every request
axiosInstance.interceptors.request.use(config => {
    const token = localStorage.getItem("Token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Track refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(token);
    });
    failedQueue = [];
};

const forceLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    window.dispatchEvent(new CustomEvent('auth:logout', {
        detail: { reason: 'Session expired' }
    }));
};

// Handle 401 errors — attempt token refresh before logging out
axiosInstance.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            console.log('[Auth] 401 on', originalRequest.url, '— attempting refresh. Token exists:', !!localStorage.getItem("Token"), 'RefreshToken exists:', !!localStorage.getItem("RefreshToken"));
            // Don't retry refresh endpoint itself
            if (originalRequest.url?.includes('RefreshToken')) {
                console.log('[Auth] Refresh endpoint itself returned 401 — forcing logout');
                forceLogout();
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Queue this request until refresh completes
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                }).catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const userId = localStorage.getItem("UserId");
            const refreshToken = localStorage.getItem("RefreshToken");

            if (!userId || !refreshToken) {
                console.log('[Auth] No userId or refreshToken in localStorage — forcing logout');
                isRefreshing = false;
                forceLogout();
                return Promise.reject(error);
            }

            try {
                const res = await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL}/User/RefreshToken`,
                    { userId: parseInt(userId), refreshToken }
                );

                const newToken = res.data.token;
                const newRefreshToken = res.data.refreshToken;

                console.log('[Auth] Token refreshed successfully');
                localStorage.setItem("Token", newToken);
                localStorage.setItem("RefreshToken", newRefreshToken);

                isRefreshing = false;
                processQueue(null, newToken);

                // Retry the original request with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                console.log('[Auth] Token refresh FAILED:', refreshError.response?.status, refreshError.message);
                isRefreshing = false;
                processQueue(refreshError, null);
                forceLogout();
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
